import React from "react";
import {
  DatePicker,
  DayOfWeek,
  DefaultButton,
  Dropdown,
  PrimaryButton,
  Stack,
} from "@fluentui/react";
import { Formik } from "formik";
import {
  addAuthorizedContacts,
  addServiceDetails,
  updateAuthorizedContacts,
  updateServiceDetails,
} from "../../../services/customers/CustomerService";
import { IAuthorizedContacts, IServiceDetails } from "../../../vm";
import { childrenGap } from "../../common/Constants";
import CustomDrawer from "../../common/CustomDrawer";
import { CustomTextField } from "../../common/Library";
import { ToastContext } from "../../common/ToastProvider";
import * as yup from "yup";

const sDSchema = yup.object().shape({
  orderId: yup.string().required("required"),
  serviceId: yup.string().required("required"),
  serviceDescription: yup.string().required("required"),
  commissionDate: yup.date().required("required"),
  datacenterId: yup.string().required("required"),
});

export interface ManageSdDialogProps {
  data?: IServiceDetails;
  handleLoading: any;
  handleDialogClose: any;
  customerId: string;
  allDatacenterDropdown: any;
}

const ManageSdDialog: React.FC<ManageSdDialogProps> = ({
  data,
  handleDialogClose,
  handleLoading,
  customerId,
  allDatacenterDropdown,
}) => {
  //state
  const [state, setState] = React.useState({
    serviceDetails: {
      orderId: "",
      serviceId: "",
      serviceDescription: "",
      commissionDate: new Date(),
      datacenterId: "",
      customerId: customerId,
      ...data,
    },
    isLoading: false,
  } as {
    serviceDetails: IServiceDetails;
    isLoading: boolean;
  });

  //hooks
  const { showToast } = React.useContext(ToastContext);

  const closeAddDialog = (data?: IServiceDetails) => {
    handleDialogClose(data);
  };
  return (
    <React.Fragment>
      <CustomDrawer
        title={
          state.serviceDetails._id
            ? "Update Service Details"
            : "Add Service Details"
        }
        handelOpen={true}
        onClose={() => closeAddDialog()}
      >
        <Stack>
          <Formik
            initialValues={state.serviceDetails}
            validationSchema={sDSchema}
            onSubmit={async (values: IServiceDetails, { setSubmitting }) => {
              let obj = { ...values };
              let result;
              handleLoading();
              if (state.serviceDetails._id) {
                result = await updateServiceDetails(obj);
              } else {
                result = await addServiceDetails(obj);
              }
              handleLoading();
              if (result.success) {
                closeAddDialog(result.data);
              } else {
                showToast(
                  result?.message ||
                    `Error while ${
                      state.serviceDetails._id ? "updating" : "adding"
                    } service details`,
                  "error"
                );
              }
              setSubmitting(false);
            }}
          >
            {({ handleSubmit, setFieldValue, values, errors, touched }) => (
              <form onSubmit={handleSubmit}>
                <Stack wrap tokens={childrenGap} className="pt-16">
                  <Stack.Item>
                    <CustomTextField name={"orderId"} label={"Order Id"} />
                  </Stack.Item>
                  <Stack.Item>
                    <CustomTextField name={"serviceId"} label={"Service Id"} />
                  </Stack.Item>
                  <Stack.Item>
                    <CustomTextField
                      name={"serviceDescription"}
                      label={"Service Description"}
                    />
                  </Stack.Item>
                  <Stack.Item className="custom-datepicker">
                    <DatePicker
                      firstDayOfWeek={DayOfWeek.Sunday}
                      value={new Date(values.commissionDate)}
                      showGoToToday={false}
                      showMonthPickerAsOverlay={true}
                      onSelectDate={(date: Date | null | undefined) =>
                        setFieldValue("commissionDate", date)
                      }
                      placeholder="Select a date..."
                      ariaLabel="Select a date"
                      label="Commission Date"
                    />
                  </Stack.Item>
                  <Stack.Item className="custom-dropdown mb-30">
                    <Dropdown
                      options={allDatacenterDropdown}
                      selectedKey={values.datacenterId}
                      onChange={(e, item) => {
                        setFieldValue("datacenterId", item?.key);
                      }}
                      label="Datacenter"
                      placeholder="Select an option"
                      errorMessage={
                        (errors.datacenterId &&
                          touched.datacenterId &&
                          errors.datacenterId) as string
                      }
                    />
                  </Stack.Item>
                  <Stack.Item>
                    <Stack
                      horizontal
                      wrap
                      tokens={childrenGap}
                      horizontalAlign="end"
                      className="pt-16"
                    >
                      <DefaultButton
                        text="Cancel"
                        onClick={() => closeAddDialog()}
                        className="borderless-btn"
                      />
                      <PrimaryButton
                        type="submit"
                        text={state.serviceDetails._id ? "Update" : "Add"}
                      />
                    </Stack>
                  </Stack.Item>
                </Stack>
              </form>
            )}
          </Formik>
        </Stack>
      </CustomDrawer>
    </React.Fragment>
  );
};

export default ManageSdDialog;
